import { USGLogo } from "@/components/auth/USGLogo";

interface AuthLayoutProps {
  children: React.ReactNode;
}

const SANS = "'Inter Tight', 'Helvetica Neue', Helvetica, Arial, sans-serif";
const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

export function AuthLayout({ children }: AuthLayoutProps) {
  return (
    <div
      className="usg-auth-grid"
      style={{
        width: "100vw",
        height: "100vh",
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        fontFamily: SANS,
        overflow: "hidden",
      }}
    >
      {/* LEFT — bone form panel */}
      <div
        style={{
          background: "#F4F1EA",
          color: "#0B1714",
          padding: "56px 80px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          overflow: "auto",
        }}
      >
        {/* Top bar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <USGLogo />
          <span
            style={{
              font: `500 10.5px/1 ${MONO}`,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "rgba(11,23,20,0.45)",
            }}
          >
            v4.2 · prod
          </span>
        </div>

        {/* Form area */}
        <div style={{ maxWidth: 420 }}>{children}</div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            color: "rgba(11,23,20,0.45)",
          }}
        >
          <span
            style={{
              font: `500 10.5px/1 ${MONO}`,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
            }}
          >
            © 2026 Urban Solution Group
          </span>
          <span
            style={{
              font: `500 10.5px/1 ${MONO}`,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
            }}
          >
            support@urbansolutiongroup.com
          </span>
        </div>
      </div>

      {/* RIGHT — dark blueprint / photo panel */}
      <div
        className="usg-auth-right"
        style={{
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(135deg, #102621 0%, #0A1714 100%)",
        }}
      >
        {/* Photo background */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: "url('/login-bg.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        {/* Dark overlay */}
        <div style={{ position: "absolute", inset: 0, background: "rgba(7,18,14,0.72)" }} />
        {/* Diagonal stripe texture */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "repeating-linear-gradient(135deg, rgba(78,211,139,0.06) 0 1px, transparent 1px 14px)",
          }}
        />
        {/* Radial green glows */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(60% 50% at 30% 30%, rgba(78,211,139,0.10), transparent 70%), radial-gradient(50% 40% at 80% 80%, rgba(110,231,160,0.08), transparent 70%)",
          }}
        />

        {/* Corner labels */}
        <div
          style={{
            position: "absolute",
            top: 24,
            left: 24,
            right: 24,
            display: "flex",
            justifyContent: "space-between",
            zIndex: 2,
          }}
        >
          <span
            style={{
              font: `500 10.5px/1 ${MONO}`,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "rgba(244,241,234,0.55)",
            }}
          >
            N 39°43′ · W 104°59′
          </span>
          <span
            style={{
              font: `500 10.5px/1 ${MONO}`,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "rgba(244,241,234,0.55)",
            }}
          >
            JOBSITE · A012
          </span>
        </div>

        {/* Center stats */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
          }}
        >
          <div style={{ textAlign: "center", color: "rgba(244,241,234,0.92)" }}>
            <div
              style={{
                font: `500 10.5px/1 ${MONO}`,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#4ED38B",
                marginBottom: 16,
              }}
            >
              {"// Hours logged · last 24h"}
            </div>
            <div
              style={{
                font: `300 132px/1 ${SANS}`,
                letterSpacing: "-0.04em",
              }}
            >
              1,284
            </div>
            <div
              style={{
                font: `500 10.5px/1 ${MONO}`,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "rgba(244,241,234,0.45)",
                marginTop: 16,
              }}
            >
              Across 47 work orders · 12 sites
            </div>
          </div>
        </div>

        {/* Bottom labels */}
        <div
          style={{
            position: "absolute",
            bottom: 24,
            left: 24,
            font: `500 10.5px/1.4 ${MONO}`,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "rgba(244,241,234,0.45)",
            zIndex: 2,
          }}
        >
          <span style={{ color: "#4ED38B", fontWeight: 500, marginRight: 8 }}>IMG</span>
          placeholder · jobsite hero
        </div>
        <div
          style={{
            position: "absolute",
            bottom: 24,
            right: 24,
            font: `500 10.5px/1 ${MONO}`,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "rgba(244,241,234,0.45)",
            zIndex: 2,
          }}
        >
          16:9 · 2400×1350
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .usg-auth-grid {
            grid-template-columns: 1fr !important;
          }
          .usg-auth-right {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
